import React, { createContext, useContext, useState, ReactNode } from 'react';
import { getStoredPersonaSlug } from '../components/chat/PersonaSelector';

interface ExpertChatContextType {
  isDrawerOpen: boolean;
  activeConversationId: number | null;
  openDrawer: () => void;
  closeDrawer: () => void;
  setConversation: (id: number | null) => void;
  /** Whether voice mode is active (Story 7.2). Session-level, not persisted. */
  voiceModeEnabled: boolean;
  setVoiceModeEnabled: (enabled: boolean) => void;
  /**
   * Whether push-to-talk voice input is enabled (Story 7.3).
   * Initialised from localStorage `voice_input_enabled`; session-level mirror.
   */
  voiceInputEnabled: boolean;
  setVoiceInputEnabled: (enabled: boolean) => void;
  /**
   * Whether transcribed voice messages are sent automatically (Story 7.3).
   * Loaded from localStorage `voice_autosend`; device preference.
   */
  autoSendVoice: boolean;
  setAutoSendVoice: (enabled: boolean) => void;
  /** Selected persona slug — persisted to localStorage. Default: 'analyst'. */
  selectedPersonaSlug: string;
  setSelectedPersonaSlug: (slug: string) => void;
}

const ExpertChatContext = createContext<ExpertChatContextType | undefined>(undefined);

export const ExpertChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [voiceModeEnabled, setVoiceModeEnabled] = useState(false);

  // Story 7.3: voice input — initialise from localStorage
  const [voiceInputEnabled, setVoiceInputEnabledState] = useState<boolean>(() => {
    try {
      return localStorage.getItem('voice_input_enabled') === 'true';
    } catch {
      return false;
    }
  });

  const [autoSendVoice, setAutoSendVoiceState] = useState<boolean>(() => {
    try {
      return localStorage.getItem('voice_autosend') === 'true';
    } catch {
      return false;
    }
  });

  const [selectedPersonaSlug, setSelectedPersonaSlugState] = useState<string>(
    () => getStoredPersonaSlug()
  );

  const setSelectedPersonaSlug = (slug: string) => {
    setSelectedPersonaSlugState(slug);
    try {
      localStorage.setItem('preferred_persona_slug', slug);
    } catch { /* ignore */ }
  };

  const setVoiceInputEnabled = (enabled: boolean) => {
    setVoiceInputEnabledState(enabled);
    try {
      localStorage.setItem('voice_input_enabled', String(enabled));
    } catch { /* ignore */ }
  };

  const setAutoSendVoice = (enabled: boolean) => {
    setAutoSendVoiceState(enabled);
    try {
      localStorage.setItem('voice_autosend', String(enabled));
    } catch { /* ignore */ }
  };

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);
  const setConversation = (id: number | null) => setActiveConversationId(id);

  return (
    <ExpertChatContext.Provider
      value={{
        isDrawerOpen,
        activeConversationId,
        openDrawer,
        closeDrawer,
        setConversation,
        voiceModeEnabled,
        setVoiceModeEnabled,
        voiceInputEnabled,
        setVoiceInputEnabled,
        autoSendVoice,
        setAutoSendVoice,
        selectedPersonaSlug,
        setSelectedPersonaSlug,
      }}
    >
      {children}
    </ExpertChatContext.Provider>
  );
};

export const useExpertChat = () => {
  const context = useContext(ExpertChatContext);
  if (!context) {
    throw new Error('useExpertChat must be used within an ExpertChatProvider');
  }
  return context;
};
